"use client";

import { useState } from "react";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { t } from "@/content/i18n/messages";
import { defaultLocale, type Locale } from "@/lib/i18n";
import MarketingConsent from "@/components/forms/MarketingConsent";
import {
  appendMarketingConsent,
  withMarketingConsentLine,
} from "@/lib/marketing-consent";
import { whatsappHref } from "@/lib/whatsapp";

const WHATSAPP = "601159246128";

// Option values are sent verbatim to Web3Forms (English is fine in the lead email);
// the visible label is translated via t().
const ISSUES = [
  "Wrong / inaccurate answers",
  "Doesn't complete the task",
  "Crashes / errors",
  "Too slow or too expensive",
  "Poor integration with our systems",
  "Vendor abandoned it",
  "No documentation or tests",
  "Other",
];
const DURATIONS = [
  "< 1 month",
  "1–3 months",
  "3–6 months",
  "6–12 months",
  "> 1 year",
];
const REPORTS = ["Yes", "No", "Not sure"];

type Status = "idle" | "loading" | "done" | "error";

export default function AgentRescueForm({
  locale = defaultLocale,
}: {
  locale?: Locale;
}) {
  const [status, setStatus] = useState<Status>("idle");
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    agent: "",
    issue: ISSUES[0],
    detail: "",
    duration: DURATIONS[1],
    report: REPORTS[2],
    builtBy: "",
  });

  const [marketingConsent, setMarketingConsent] = useState(false);

  const set = (k: keyof typeof form, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const key = process.env.NEXT_PUBLIC_WEB3FORMS_KEY;
    if (!key || key === "your_actual_web3forms_key_here") {
      setStatus("error");
      return;
    }
    setStatus("loading");
    const message =
      `AI Agent Rescue audit request — ${form.company}.\n` +
      `What the agent does: ${form.agent}\n` +
      `Main issue: ${form.issue}\n` +
      `What doesn't work: ${form.detail}\n` +
      `Running for: ${form.duration}\n` +
      `Has quality/eval report: ${form.report}\n` +
      `Built by: ${form.builtBy || "not stated"}`;
    try {
      const body = new FormData();
      body.append("access_key", key);
      body.append("name", form.name);
      body.append("company", form.company);
      body.append("email", form.email);
      body.append("request_type", "Agent Rescue Audit");
      body.append("source", "agent-rescue");
      body.append("subject", `AI Agent Rescue audit — ${form.company}`);
      body.append("from_name", "Anchor Sprint Website");
      body.append("agent_purpose", form.agent);
      body.append("main_issue", form.issue);
      body.append("what_fails", form.detail);
      body.append("project_duration", form.duration);
      body.append("has_eval_report", form.report);
      body.append("built_by", form.builtBy);
      body.append("message", withMarketingConsentLine(message, marketingConsent));
      appendMarketingConsent(body, marketingConsent, locale);
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body,
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || "Web3Forms submission failed");
      }
      trackEvent("generate_lead", {
        category: "lead",
        label: "agent_rescue",
        source: "agent-rescue",
        email_provided: true,
      });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  }

  if (status === "done") {
    return (
      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" aria-hidden />
        <h3 className="mt-4 font-display text-xl font-semibold text-neutral-emphasis">
          {t(locale, "Request received — we'll be in touch")}
        </h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-neutral-muted">
          {t(
            locale,
            "We'll review what you sent and come back with an honest read on whether your agent can be rectified — usually within one business day.",
          )}
        </p>
      </div>
    );
  }

  const fieldCls =
    "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-neutral-emphasis outline-none transition focus:border-primary";
  const labelCls =
    "mb-1.5 block text-sm font-medium text-neutral-emphasis";

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="ar-name">
            {t(locale, "Your name")}
          </label>
          <input
            id="ar-name"
            required
            className={fieldCls}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="ar-company">
            {t(locale, "Company")}
          </label>
          <input
            id="ar-company"
            required
            className={fieldCls}
            value={form.company}
            onChange={(e) => set("company", e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className={labelCls} htmlFor="ar-email">
          {t(locale, "Work email")}
        </label>
        <input
          id="ar-email"
          type="email"
          required
          className={fieldCls}
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
        />
      </div>

      <div>
        <label className={labelCls} htmlFor="ar-agent">
          {t(locale, "What does the agent do?")}
        </label>
        <input
          id="ar-agent"
          required
          placeholder={t(locale, "e.g. answers customer orders on WhatsApp")}
          className={fieldCls}
          value={form.agent}
          onChange={(e) => set("agent", e.target.value)}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="ar-issue">
            {t(locale, "Main issue")}
          </label>
          <select
            id="ar-issue"
            className={fieldCls}
            value={form.issue}
            onChange={(e) => set("issue", e.target.value)}
          >
            {ISSUES.map((o) => (
              <option key={o} value={o}>
                {t(locale, o)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="ar-duration">
            {t(locale, "How long has it been running?")}
          </label>
          <select
            id="ar-duration"
            className={fieldCls}
            value={form.duration}
            onChange={(e) => set("duration", e.target.value)}
          >
            {DURATIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls} htmlFor="ar-detail">
          {t(locale, "What specifically doesn't work?")}
        </label>
        <textarea
          id="ar-detail"
          rows={3}
          className={fieldCls}
          value={form.detail}
          onChange={(e) => set("detail", e.target.value)}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="ar-report">
            {t(locale, "Do you have a quality / eval report?")}
          </label>
          <select
            id="ar-report"
            className={fieldCls}
            value={form.report}
            onChange={(e) => set("report", e.target.value)}
          >
            {REPORTS.map((o) => (
              <option key={o} value={o}>
                {t(locale, o)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="ar-builtby">
            {t(locale, "Who built it? (optional)")}
          </label>
          <input
            id="ar-builtby"
            className={fieldCls}
            placeholder={t(locale, "vendor or platform")}
            value={form.builtBy}
            onChange={(e) => set("builtBy", e.target.value)}
          />
        </div>
      </div>

      {status === "error" && (
        <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-neutral-emphasis">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
          <span>
            {t(locale, "Something went wrong sending that. Please")}{" "}
            <a
              href={whatsappHref(
                "Hi Anchor Sprint — I'd like a free AI Agent Rescue audit.",
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-track-event="contact_whatsapp_click"
              data-track-location="agent_rescue_form_error"
              data-track-phone={WHATSAPP}
              className="font-semibold text-primary underline"
            >
              {t(locale, "message us on WhatsApp")}
            </a>{" "}
            {t(locale, "and we'll pick it up.")}
          </span>
        </div>
      )}

      <MarketingConsent
        id="ar-marketing-consent"
        checked={marketingConsent}
        onChange={setMarketingConsent}
        locale={locale}
      />

      <button
        type="submit"
        disabled={status === "loading"}
        data-track-event="cta_click"
        data-track-location="agent_rescue_form_submit"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-base font-semibold text-primary-foreground transition hover:bg-primary-emphasis disabled:opacity-60 sm:w-auto"
      >
        {status === "loading"
          ? t(locale, "Sending…")
          : t(locale, "Get my free agent audit")}
        {status !== "loading" && <ArrowRight className="h-4 w-4" aria-hidden />}
      </button>
      <p className="text-xs text-neutral-muted">
        {t(
          locale,
          "Free, no obligation. We'll tell you honestly whether it's worth rectifying or rebuilding.",
        )}
      </p>
    </form>
  );
}
